/// <reference path="../../../../../typings/index.d.ts" />
/// <reference path="../controls.module.ts" />

module Meevo.Components.Controls {
    'use strict';
    export type LimitDateTimeRangeType = 'Days' | 'Week' | 'Months' | 'Years' | 'Quarter';


    export interface IValidationRuleObj {
        rule: PresetValidationRule;
        data?: any[];
    }

    interface IRules {
        maxDate: PresetValidationRule;
        minDate: PresetValidationRule;
        maxDateReport: PresetValidationRule;
        minDateReport: PresetValidationRule;
    }

    interface ICustomRules {
        [key: string]: PresetValidationRule;
    }

    interface ILimitDateRangeToRules extends ICustomRules {
        Days: PresetValidationRule;
        Week: PresetValidationRule;
        Months: PresetValidationRule;
        Years: PresetValidationRule;
        Quarter: PresetValidationRule;
    }

    export type PresetValidationRule = (from: Date, to: Date, ...args: any[]) => boolean;

    export const RULES: IRules = {
        maxDate: (from: Date, to: Date, dates: Date) => !moment(to).isAfter(dates, 'day') || to === undefined,
        minDate: (from: Date, to: Date, dates: Date) => moment(from).isAfter(dates, 'day') || from === undefined,
        maxDateReport: (from: Date, to: Date, dates: Date) => moment(from).add(-1, 'days').isBefore(dates, 'day') || from === undefined,
        minDateReport: (from: Date, to: Date, dates: Date) => moment(to).add(1, 'days').isAfter(dates, 'day') || to === undefined,
    };


    /**
     * Reports should only display the relative dates that apply to them based on their throttle definition
     * When can a report be run for? (Parameter to use in metadata is RunReportFor)
     *
     */
    export const RunReportForRules: ICustomRules = {
        /**
         Past (Metadata Parameter is PastToday)

         Today
         Yesterday
         This Week
         Last Week
         This Month
         Last Month
         This Quarter
         Last Quarter
         This Year
         Last Year
         */
        PastToday: (from: Date, to: Date) => moment(from).isBefore(new Date()) || from === undefined,


        /**
         Past (Not Incl. Current Date)

         Yesterday
         This Week
         Last Week
         This Month
         Last Month
         This Quarter
         Last Quarter
         This Year
         Last Year
         */
        PastNotToday: (from: Date, to: Date) => moment(from).isBefore(moment().add(-1, 'day').toDate()) || from === undefined,


        /**
         Past (Not Incl. Current Date)
         
         This Week
         Last Week
         This Month
         Last Month
         This Quarter
         Last Quarter
         This Year
         Last Year
         */
        PastNotTodayAndYesterday: (from: Date, to: Date) => moment(from).isBefore(moment().add(-2, 'day').toDate()) || from === undefined,


        /**
         Future

         Today
         Tomorrow
         This Week
         Next Week
         This Month
         Next Month
         This Quarter
         Next Quarter
         This Year
         Next Year
         */
        FutureToday: (from: Date, to: Date) => moment(to).isAfter(new Date()) || to === undefined,


        /**
         Future (Not Incl. Current Day)

         Tomorrow
         This Week
         Next Week
         This Month
         Next Month
         This Quarter
         Next Quarter
         This Year
         Next Year
         */
        FutureNotToday: (from: Date, to: Date) => {
            return moment(to).isAfter(moment().add(1, 'day').toDate()) || to === undefined;
        },


        /**
         Both Past and Future

         Today
         Yesterday
         Tomorrow
         This Week
         Last Week
         Next Week
         This Month
         Last Month
         Next Month
         This Quarter
         Last Quarter
         Next Quarter
         This Year
         Last Year
         Next Year
         */
        All: (from: Date, to: Date) => {
            return true;
        },
    };


    /**
     * Date Throttle (At one time what is the maximum amount of days in which a report can be run.)
     * (Parameter to use in MetaData is LimitDateRangeTo)
     * This control will limit the Days/Week/Month/Quarter/Year option within the relative date control in conjunction
     * with the report run setting definition. A "sliding range," will be used based on the start date as to when a
     * report can be run for. A user should not be limited to how far in the past they can run the report for but
     * rather, at one time, after I select my start date, how far ahead of the start date can I run the report for.
     */
    export const LimitDateRangeToRules: ILimitDateRangeToRules = {

        /**
         * X Days
         * A count of calendar day
         */
        Days: (from: Date, to: Date, numberOfDays: number): boolean => {
            return moment(to).diff(moment(from), 'days') <= numberOfDays;
        },

        /**
         * X Week
         * (7 calendar days * X number of weeks) based on the start date of the report
         */
        Week: (from: Date, to: Date, numberOfWeeks: number): boolean => {
            return moment(to).diff(moment(from), 'weeks', true) <= numberOfWeeks;
        },

        /**
         * X Months
         * A month, due to the length of time of the longest month, is defined as 31 days. To calculate the throttle, the following formula will be used:
         * (31 days * X number of months)
         * When a report is able to be run for 3 months or greater, that report should then see the quarter option.
         */
        Months: (from: Date, to: Date, numberOfMonths: number): boolean => {
            return moment(to).diff(moment(from), 'months', true) <= numberOfMonths;
        },

        Quarter: (from: Date, to: Date, numberOfQuarters: number): boolean => {
            return moment(to).diff(moment(from), 'days') <= numberOfQuarters * 93;
        },

        /**
         * X Years
         * One year, due to leap years, is defined as 366 days. To calculate the throttle, the following formula will be used:
         * (366 days * X number of years)
         */
        Years: (from: Date, to: Date, numberOfYears: number): boolean => {
            return moment(to).diff(moment(from), 'years', true) <= numberOfYears;
        },
    };
}
